import React from "react";
import { useReveal } from "../hooks/useReveal";

/* ─────────────────────────────────────────────────────────────
 * Dynamic SVG visuals — one per benefit. These read like a
 * product/tech HUD rather than stock photos.
 * ────────────────────────────────────────────────────────────*/

function RadarVisual() {
  return (
    <svg viewBox="0 0 600 360" className="w-full h-full">
      <defs>
        <radialGradient id="rd" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#1FB3FF" stopOpacity="0.35" />
          <stop offset="60%" stopColor="#1FB3FF" stopOpacity="0.05" />
          <stop offset="100%" stopColor="#1FB3FF" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="sweep" x1="0" x2="1">
          <stop offset="0%" stopColor="#3FE2FF" stopOpacity="0" />
          <stop offset="100%" stopColor="#3FE2FF" stopOpacity="0.9" />
        </linearGradient>
      </defs>
      <rect width="600" height="360" fill="url(#rd)" />
      {/* concentric rings */}
      {[40, 90, 140, 190].map((r) => (
        <circle
          key={r}
          cx="300"
          cy="180"
          r={r}
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="1"
          fill="none"
        />
      ))}
      {/* crosshair */}
      <line x1="100" y1="180" x2="500" y2="180" stroke="rgba(255,255,255,0.05)" />
      <line x1="300" y1="20" x2="300" y2="340" stroke="rgba(255,255,255,0.05)" />

      {/* rotating sweep */}
      <g style={{ transformOrigin: "300px 180px", animation: "spin 4.5s linear infinite" }}>
        <path
          d="M300 180 L300 30 A150 150 0 0 1 410 80 Z"
          fill="url(#sweep)"
          opacity="0.45"
        />
      </g>

      {/* pulsing target */}
      <circle cx="300" cy="180" r="6" fill="#3FE2FF">
        <animate attributeName="r" values="6;10;6" dur="2.2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="1;0.6;1" dur="2.2s" repeatCount="indefinite" />
      </circle>
      <circle cx="300" cy="180" r="14" stroke="#3FE2FF" strokeWidth="1" fill="none">
        <animate attributeName="r" values="14;28;14" dur="2.2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.8;0;0.8" dur="2.2s" repeatCount="indefinite" />
      </circle>

      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </svg>
  );
}

function CalmVisual() {
  // Chaotic waveform smoothing into a calm baseline.
  const chaos =
    "M0,180 L30,90 L50,250 L75,60 L100,260 L125,80 L150,240 L175,100 L200,220 L225,120 L250,200 L275,140 L300,190 L325,170 L350,185 L375,175 L400,182 L450,180 L500,180 L600,180";
  const calm =
    "M0,180 L60,178 L120,184 L180,176 L240,182 L300,178 L360,182 L420,178 L480,182 L540,178 L600,180";

  return (
    <svg viewBox="0 0 600 360" className="w-full h-full">
      <defs>
        <linearGradient id="wave" x1="0" x2="1">
          <stop offset="0%" stopColor="#1FB3FF" stopOpacity="0" />
          <stop offset="20%" stopColor="#3FE2FF" stopOpacity="1" />
          <stop offset="80%" stopColor="#1FB3FF" stopOpacity="1" />
          <stop offset="100%" stopColor="#1FB3FF" stopOpacity="0" />
        </linearGradient>
        <radialGradient id="cglow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#1FB3FF" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#1FB3FF" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="600" height="360" fill="url(#cglow)" />

      {/* grid baseline */}
      {[120, 180, 240].map((y) => (
        <line key={y} x1="0" y1={y} x2="600" y2={y} stroke="rgba(255,255,255,0.05)" />
      ))}

      {/* breathing oscillation */}
      <path
        d={chaos}
        fill="none"
        stroke="url(#wave)"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.85"
      >
        <animate
          attributeName="d"
          values={`${chaos};${calm};${chaos}`}
          dur="6s"
          repeatCount="indefinite"
          calcMode="spline"
          keySplines="0.4 0 0.2 1; 0.4 0 0.2 1"
        />
      </path>

      {/* trailing dot following the wave centre */}
      <circle r="4" fill="#3FE2FF">
        <animateMotion
          dur="6s"
          repeatCount="indefinite"
          rotate="auto"
          path="M0,180 Q150,150 300,180 T600,180"
        />
        <animate attributeName="opacity" values="0.4;1;0.4" dur="2.4s" repeatCount="indefinite" />
      </circle>

      {/* slow breathing ring */}
      <circle cx="300" cy="180" r="40" fill="none" stroke="#3FE2FF" strokeWidth="1" opacity="0.4">
        <animate attributeName="r" values="40;72;40" dur="5.2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.45;0.08;0.45" dur="5.2s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

function EqualizerVisual() {
  // 8 vertical bars — varying heights animate softly
  const bars = [0, 1, 2, 3, 4, 5, 6, 7];
  return (
    <svg viewBox="0 0 600 360" className="w-full h-full">
      <defs>
        <linearGradient id="bg" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#3FE2FF" />
          <stop offset="100%" stopColor="#1FB3FF" />
        </linearGradient>
        <radialGradient id="glow" cx="50%" cy="80%" r="50%">
          <stop offset="0%" stopColor="#1FB3FF" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#1FB3FF" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="600" height="360" fill="url(#glow)" />

      {/* horizontal grid */}
      {[80, 140, 200, 260].map((y) => (
        <line key={y} x1="40" y1={y} x2="560" y2={y} stroke="rgba(255,255,255,0.05)" />
      ))}

      {/* bars */}
      {bars.map((i) => {
        const x = 80 + i * 60;
        const baseH = [120, 90, 180, 60, 200, 140, 100, 160][i];
        const dur = 1.6 + (i % 3) * 0.4;
        return (
          <g key={i}>
            {/* track */}
            <rect x={x - 10} y="70" width="20" height="220" rx="10" fill="rgba(255,255,255,0.04)" />
            {/* animated bar */}
            <rect x={x - 10} y={290 - baseH} width="20" height={baseH} rx="10" fill="url(#bg)">
              <animate
                attributeName="height"
                values={`${baseH};${baseH * 0.4};${baseH * 1.1};${baseH * 0.6};${baseH}`}
                dur={`${dur}s`}
                repeatCount="indefinite"
              />
              <animate
                attributeName="y"
                values={`${290 - baseH};${290 - baseH * 0.4};${290 - baseH * 1.1};${290 - baseH * 0.6};${290 - baseH}`}
                dur={`${dur}s`}
                repeatCount="indefinite"
              />
            </rect>
            {/* head dot */}
            <circle cx={x} cy={290 - baseH} r="3" fill="#3FE2FF">
              <animate
                attributeName="cy"
                values={`${290 - baseH};${290 - baseH * 0.4};${290 - baseH * 1.1};${290 - baseH * 0.6};${290 - baseH}`}
                dur={`${dur}s`}
                repeatCount="indefinite"
              />
            </circle>
          </g>
        );
      })}
    </svg>
  );
}

const BENEFITS = [
  {
    label: "Focus",
    desc: "Tunnel vision when distraction won't help.",
    caption: "Tunnel vision",
    Visual: RadarVisual,
    icon: (
      <svg viewBox="0 0 64 64" fill="none">
        <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="1" />
        <circle cx="32" cy="32" r="18" stroke="currentColor" strokeWidth="1" />
        <circle cx="32" cy="32" r="8" stroke="currentColor" strokeWidth="1" />
        <circle cx="32" cy="32" r="2.5" fill="currentColor" />
      </svg>
    ),
  },
  {
    label: "Calm",
    desc: "Composure when pressure rises.",
    caption: "Composure",
    Visual: CalmVisual,
    icon: (
      <svg viewBox="0 0 64 64" fill="none">
        <path
          d="M4 32 L18 32 L24 18 L32 46 L40 24 L46 32 L60 32"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    label: "Control",
    desc: "Intentional energy. Clear decisions. Locked in.",
    caption: "Intentional energy",
    Visual: EqualizerVisual,
    icon: (
      <svg viewBox="0 0 64 64" fill="none">
        <rect
          x="6"
          y="22"
          width="52"
          height="20"
          rx="10"
          stroke="currentColor"
          strokeWidth="1"
        />
        <circle cx="42" cy="32" r="6" fill="currentColor" />
      </svg>
    ),
  },
];

const STATS = [
  ["Onset", "≈ 90s"],
  ["Crash", "Zero"],
  ["Stack", "Clean"],
];

export default function Benefits() {
  const headRef = useReveal();
  return (
    <section
      id="benefits"
      data-testid="benefits-section"
      className="section relative"
    >
      <div className="container-x relative z-10">
        <div ref={headRef} className="reveal mb-12 lg:mb-16 max-w-3xl">
          <span className="font-mono text-sm uppercase tracking-[0.3em] text-white/55">
            / 08 — The Effect
          </span>
          <h2
            className="kinetic mt-6"
            style={{ fontSize: "clamp(2.6rem, 6vw, 5.5rem)", lineHeight: 0.95 }}
          >
            <span className="gradient-text">THREE SWITCHES.</span>
            <br />
            <span className="outline-text-blue">ONE CHEW.</span>
          </h2>
          <p className="mt-6 text-white/55 text-base lg:text-lg max-w-xl">
            A chew that thinks ahead. Engineered for the 90 seconds before pressure arrives.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {BENEFITS.map((b, i) => (
            <BenefitCard key={i} b={b} i={i} />
          ))}
        </div>

        {/* Onset / Crash / Stack rail */}
        <div className="mt-16 grid sm:grid-cols-3 gap-px bg-white/5 border border-white/5 rounded-2xl overflow-hidden">
          {STATS.map(([k, v], i) => (
            <StatCell key={i} k={k} v={v} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function BenefitCard({ b, i }) {
  const ref = useReveal();
  return (
    <div
      ref={ref}
      data-testid={`benefit-${i}`}
      className="reveal card-dark p-0 h-full flex flex-col group relative overflow-hidden"
    >
      {/* Header visual */}
      <div className="relative aspect-[16/10] overflow-hidden border-b border-white/8 bg-[#03060a]">
        {b.image ? (
          <img
            src={b.image}
            alt={b.caption}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover grayscale-[35%] contrast-110 group-hover:grayscale-0 group-hover:scale-[1.04] transition-all duration-700 ease-out"
          />
        ) : (
          <div className="absolute inset-0 w-full h-full">
            {b.Visual && <b.Visual />}
          </div>
        )}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(2,3,5,0.0) 30%, rgba(2,3,5,0.85) 100%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-60"
          style={{
            background:
              "radial-gradient(circle at 80% 20%, rgba(31,179,255,0.45), transparent 60%)",
          }}
        />
        {/* Bottom caption rail (no top labels — they collided with the SVG art) */}
        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10" style={{ color: "var(--blue-bright)" }}>
              {b.icon}
            </div>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">
              / 0{i + 1}
            </span>
          </div>
          <span
            className="font-mono text-[10px] uppercase tracking-[0.25em]"
            style={{ color: "var(--blue-bright)" }}
          >
            {b.caption}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-8 lg:p-10 flex-1 flex flex-col">
        <h3 className="font-display text-5xl lg:text-6xl uppercase text-white mb-4">
          {b.label}
        </h3>
        <p className="text-white/55 text-base lg:text-lg leading-relaxed">{b.desc}</p>
      </div>

      <div
        className="absolute -bottom-20 -right-20 w-48 h-48 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(31,179,255,0.4), transparent)" }}
      />
    </div>
  );
}

function StatCell({ k, v, i }) {
  const ref = useReveal();
  return (
    <div
      ref={ref}
      data-testid={`benefit-stat-${i}`}
      className="reveal bg-[#050608] p-8 lg:p-10 hover:bg-[#070a10] transition-colors duration-500"
    >
      <div className="font-mono text-[0.7rem] uppercase tracking-[0.3em] text-white/45">
        {k}
      </div>
      <div
        className="font-display text-5xl lg:text-6xl mt-3"
        style={{ color: "var(--blue-bright)" }}
      >
        {v}
      </div>
    </div>
  );
}
